import { BaseService } from './base-service';
import { UserProfile } from '../types';

export class ProfileService extends BaseService {
  public async getUserProfile(): Promise<UserProfile> {
    await this.heyUtils.waitInit();
    if (this.heyUtils._cachedProfile) {
      if (Date.now() - this.heyUtils._cachedProfileTimestamp < 1000 * 60 * 5) {
        console.debug('Returning cached user profile');
        return this.heyUtils._cachedProfile;
      } else {
        this.heyUtils._cachedProfile = null;
      }
    }
    if (!this.heyUtils.user_id) {
      console.error("User ID invalid, skiping call");
      return null;
    }
    try {
      console.debug(`📊 GET USER PROFILE REQUEST: /v3/user/${this.heyUtils.user_id}`);
      let result = await this.callHeyApiUser('GET', `/v3/user/${this.heyUtils.user_id}`, null);
      
      if (!result.success || result?.data?.user === undefined) {
        console.error("📊 GET USER PROFILE FAILED:", result?.error || "User not found in response");
        return null;
      }
      
      console.debug("📊 GET USER PROFILE SUCCESS:", JSON.stringify(result.data, null, 2));
      this.heyUtils._cachedProfile = result.data as UserProfile;
      this.heyUtils._cachedProfileTimestamp = Date.now();
      
      // Set language and currency preferences
      this.setUserPreferences(this.heyUtils._cachedProfile);
      
      return this.heyUtils._cachedProfile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  private setUserPreferences(profile: UserProfile): void {
    if (!profile || !profile.user) return;
    
    // Set language preference
    if (profile.user.language) {
      console.debug(`🌐 Setting language from user profile: ${profile.user.language}`);
      try {
        localStorage.setItem('user_preferred_language', profile.user.language);
      } catch (error) {
        console.error('Error setting user language in localStorage:', error);
        localStorage.clear();
      }
      // Import dynamically to avoid circular dependencies
      import('@/i18n').then(i18nModule => {
        if (i18nModule.default.languages.includes(profile.user.language)) {
          i18nModule.default.changeLanguage(profile.user.language);
        }
      }).catch(err => console.error('Failed to import i18n module:', err));
    }
    
    // Set currency preference
    if (profile.user.currency) {
      console.debug(`💰 Setting currency from user profile: ${profile.user.currency}`);
      try {
        localStorage.setItem('user_preferred_currency', profile.user.currency);
      } catch (error) {
        console.error('Error setting user currency in localStorage:', error);
        localStorage.clear();
      }
    }
  }

  public async updateUser(user: UserProfile): Promise<boolean> {
    console.debug("📊 UPDATE USER REQUEST:", JSON.stringify(user, null, 2));
    let result = await this.callHeyApiUser('PATCH', `/v3/user/${this.heyUtils.user_id}`, user);
    
    if (!result.success) {
      console.error("📊 UPDATE USER FAILED:", result.error);
      throw new Error('Failed to update user profile.' + result.error);
    }
    
    console.debug("📊 UPDATE USER SUCCESS:", JSON.stringify(result.data, null, 2));
    this.heyUtils._cachedProfile = result.data as UserProfile;
    this.heyUtils._cachedProfileTimestamp = Date.now();
    
    // Update language and currency preferences
    this.setUserPreferences(this.heyUtils._cachedProfile);
    
    return result.success;
  }

  public async deleteAccount(): Promise<boolean> {
    console.debug("🗑️ DELETE ACCOUNT REQUEST for user:", this.heyUtils.user_id);
    try {
      // Call the delete user API with user token authorization instead of tech token
      let result = await this.callHeyApiUser('DELETE', `/v3/user/${this.heyUtils.user_id}`, null);
      
      if (!result.success) {
        console.error("🗑️ DELETE ACCOUNT FAILED:", result.error);
        throw new Error('Failed to delete account: ' + result.error);
      }
      
      console.debug("🗑️ DELETE ACCOUNT SUCCESS");
      
      // Clear user data and sign out
      await this.heyUtils.signOut();
      
      return true;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }
}
